import React from 'react';
import {
  Facebook,
  InstagramReels,
  InstagramStories,
  PlusBold,
  TiktokLogo,
} from 'components/icons';
import {
  BlueskySocialCircle,
  FacebookSocialCircle,
  InstagramSocialCircle,
  LinkedinSocialCircle,
  ThreadsSocialCircle,
  TikTokSocialCircle,
  XSocialCircle,
  YouTubeSocialCircle,
} from 'components/SocialCircle';
import {
  BLACK,
  FACEBOOK_BLUE,
  HEADLINER_GRADIENT,
  INSTAGRAM_GRADIENT,
} from 'constants/colors';
import {
  DestinationPlatform,
  DestinationPlatformMap,
  DestinationPreset,
  DestinationPresetsConfig,
  DestinationPresetsDefaultContent,
  MaxDurationKey,
  PlatformOptions,
} from './types';

export const DESTINATION_PLATFORMS: DestinationPlatform[] = [
  'youtube',
  'instagram',
  'facebook',
  'tiktok',
  'linkedin',
  'threads',
  'twitter',
  'bluesky',
];

export const PLATFORM_DISPLAY_NAMES: Record<DestinationPlatform, string> = {
  youtube: 'YouTube',
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
  threads: 'Threads',
  twitter: 'X (Twitter)',
  bluesky: 'Bluesky',
  snapchat: 'Snapchat',
  pinterest: 'Pinterest',
};

export const MAX_DURATION_VALID_KEYS: MaxDurationKey[] = [
  'YouTubeShort',
  'InstagramReel',
  'InstagramStory',
  'InstagramPost',
  'FacebookReel',
  'FacebookStory',
  'FacebookPost',
  'TikTokPost',
  'TikTokStory',
];

export const DESTINATION_PRESETS_DURATION_MILLIS = {
  FIFTEEN_SECS: 15000,
  TWENTY_SECS: 20000,
  THIRTY_SECS: 30000,
  SIXTY_SECS: 60000,
  NINETY_SECS: 90000,
  TWO_MINUTES_TWENTY_SECS: 140000,
  THREE_MINUTES: 180000,
  TEN_MINUTES: 600000,
} as const;

export const DESTINATION_PRESETS_DEFAULT_CONTENT: DestinationPresetsDefaultContent = {
  youTubeEpisode: {
    title: 'Full Episode',
    description: 'Add an entire podcast episode',
    aspectRatio: 'landscape',
    durationMs: DESTINATION_PRESETS_DURATION_MILLIS.THREE_MINUTES,
  },
  youTubeClip: {
    title: 'Episode Clip',
    description: 'Create longer clips',
    aspectRatio: 'portrait',
    durationMs: DESTINATION_PRESETS_DURATION_MILLIS.NINETY_SECS,
  },
  youTubeShort: {
    title: 'YouTube Short',
    description: 'Create short Shorts!',
    aspectRatio: 'portrait',
    durationMs: DESTINATION_PRESETS_DURATION_MILLIS.FIFTEEN_SECS,
  },
  instagramReel: {
    title: 'Instagram Reel',
    description: 'Create short Reels from your podcast',
    aspectRatio: 'portrait',
    durationMs: DESTINATION_PRESETS_DURATION_MILLIS.THIRTY_SECS,
  },
  instagramStory: {
    title: 'Instagram Story',
    description: 'Share a clip to your story',
    aspectRatio: 'portrait',
    durationMs: DESTINATION_PRESETS_DURATION_MILLIS.FIFTEEN_SECS,
  },
  instagramPost: {
    title: 'Instagram Post',
    description: 'Create clips from your podcast',
    aspectRatio: 'square',
    durationMs: DESTINATION_PRESETS_DURATION_MILLIS.SIXTY_SECS,
  },
  facebookReel: {
    title: 'Facebook Reel',
    description: 'Create short Reels from your podcast',
    aspectRatio: 'portrait',
    durationMs: DESTINATION_PRESETS_DURATION_MILLIS.FIFTEEN_SECS,
  },
  facebookStory: {
    title: 'Facebook Story',
    description: 'Share a clip to your story',
    aspectRatio: 'portrait',
    durationMs: DESTINATION_PRESETS_DURATION_MILLIS.FIFTEEN_SECS,
  },
  facebookPost: {
    title: 'Facebook Post',
    aspectRatio: 'portrait',
    durationMs: DESTINATION_PRESETS_DURATION_MILLIS.TEN_MINUTES,
  },
  facebookEpisode: {
    title: 'Full Episode',
    description: 'Add an entire podcast episode',
    aspectRatio: 'landscape',
  },
  tikTokPost: {
    title: 'TikTok Post',
    description: 'Share short clips from your podcast',
    aspectRatio: 'portrait',
    durationMs: DESTINATION_PRESETS_DURATION_MILLIS.THIRTY_SECS,
  },
  tikTokStory: {
    title: 'TikTok Story',
    description: 'Share a clip to your story',
    aspectRatio: 'portrait',
    durationMs: DESTINATION_PRESETS_DURATION_MILLIS.FIFTEEN_SECS,
  },
  linkedInFeedVideo: {
    title: 'LinkedIn Feed Video',
    description: 'Clip a full segment from an episode',
    aspectRatio: 'landscape',
  },
  linkedInPost: {
    title: 'LinkedIn Post',
    description: 'Share short clips from your podcast',
    aspectRatio: 'square',
    durationMs: DESTINATION_PRESETS_DURATION_MILLIS.SIXTY_SECS,
  },
  linkedInShort: {
    title: 'LinkedIn Short',
    description: 'Create a mobile-first video from your podcast',
    aspectRatio: 'portrait',
    durationMs: DESTINATION_PRESETS_DURATION_MILLIS.THIRTY_SECS,
  },
  threadsReel: {
    title: 'Threads Reel',
    description: 'Create short Reels from your podcast',
    aspectRatio: 'portrait',
    durationMs: DESTINATION_PRESETS_DURATION_MILLIS.THIRTY_SECS,
  },
  threadsPost: {
    title: 'Threads Post',
    description: 'Share short clips from your podcast',
    aspectRatio: 'square',
    durationMs: DESTINATION_PRESETS_DURATION_MILLIS.SIXTY_SECS,
  },
  twitterStandardTimeline: {
    title: 'X Standard Timeline',
    description: 'Create clips from your podcast',
    aspectRatio: 'landscape',
    durationMs: DESTINATION_PRESETS_DURATION_MILLIS.SIXTY_SECS,
  },
  twitterPost: {
    title: 'X Post',
    description: 'Share short clips from your podcast',
    aspectRatio: 'square',
    durationMs: DESTINATION_PRESETS_DURATION_MILLIS.THIRTY_SECS,
  },
  twitterVideoTab: {
    title: 'X Video Tab',
    description: 'Create a mobile-first video from your podcast',
    aspectRatio: 'portrait',
    durationMs: DESTINATION_PRESETS_DURATION_MILLIS.FIFTEEN_SECS,
  },
  blueskyPost: {
    title: 'BlueSky Post',
    description: 'Create clips from your podcast',
    aspectRatio: 'landscape',
    durationMs: DESTINATION_PRESETS_DURATION_MILLIS.SIXTY_SECS,
  },
  blueskyShortClip: {
    title: 'BlueSky Short Clip',
    description: 'Share short clips from your podcast',
    aspectRatio: 'square',
    durationMs: DESTINATION_PRESETS_DURATION_MILLIS.THIRTY_SECS,
  },
  blueskyVideoFeeds: {
    title: 'BlueSky Video Feeds',
    description: 'Create a mobile-first video from your podcast',
    aspectRatio: 'portrait',
    durationMs: DESTINATION_PRESETS_DURATION_MILLIS.FIFTEEN_SECS,
  },
};

export const MAX_PLATFORM_DURATION_AS_SECS = 90;

export const YOUTUBE_EPISODE_PRESET: DestinationPreset = {
  key: 'YouTubeEpisode',
  type: 'episode',
  title: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeEpisode.title,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeEpisode.title,
    videoTranscript: 'Episode Segment',
  },
  description: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeEpisode.description,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeEpisode.description,
    videoTranscript: 'Create full-length segments',
  },
  aspectRatio: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeEpisode.aspectRatio,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeEpisode.aspectRatio,
    videoTranscript: 'portrait',
  },
  durationMs: {
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeEpisode.durationMs,
  },
};

export const YOUTUBE_CLIP_PRESET: DestinationPreset = {
  key: 'YouTubeClip',
  type: 'clip',
  title: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeClip.title,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeClip.title,
    videoTranscript: DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeClip.title,
  },
  description: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeClip.description,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeClip.description,
    videoTranscript: 'Create clips from your podcast',
  },
  aspectRatio: {
    audioClipper: DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeClip.aspectRatio,
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeClip.aspectRatio,
    automation: 'portrait',
    videoTranscript: 'portrait',
  },
  durationMs: {
    audioClipper: DESTINATION_PRESETS_DURATION_MILLIS.TEN_MINUTES,
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeClip.durationMs,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeClip.durationMs,
    videoTranscript: DESTINATION_PRESETS_DURATION_MILLIS.SIXTY_SECS,
  },
};

export const YOUTUBE_SHORT_PRESET: DestinationPreset = {
  key: 'YouTubeShort',
  type: 'short',
  title: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeShort.title,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeShort.title,
    videoTranscript: DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeShort.title,
  },
  description: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeShort.description,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeShort.description,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeShort.description,
  },
  aspectRatio: {
    audioClipper: DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeShort.aspectRatio,
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeShort.aspectRatio,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeShort.aspectRatio,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeShort.aspectRatio,
  },
  durationMs: {
    audioClipper: DESTINATION_PRESETS_DURATION_MILLIS.SIXTY_SECS,
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeShort.durationMs,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeShort.durationMs,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.youTubeShort.durationMs,
  },
};

export const INSTAGRAM_REEL_PRESET: DestinationPreset = {
  key: 'InstagramReel',
  type: 'reel',
  icon: <InstagramReels width="15px" color="white" />,
  title: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramReel.title,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramReel.title,
    videoTranscript: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramReel.title,
  },
  description: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramReel.description,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramReel.description,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.instagramReel.description,
  },
  aspectRatio: {
    audioClipper: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramReel.aspectRatio,
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramReel.aspectRatio,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramReel.aspectRatio,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.instagramReel.aspectRatio,
  },
  durationMs: {
    audioClipper: DESTINATION_PRESETS_DURATION_MILLIS.NINETY_SECS,
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramReel.durationMs,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramReel.durationMs,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.instagramReel.durationMs,
  },
};

export const INSTAGRAM_STORY_PRESET: DestinationPreset = {
  key: 'InstagramStory',
  type: 'story',
  icon: <InstagramStories width="15px" color="white" />,
  title: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramStory.title,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramStory.title,
    videoTranscript: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramStory.title,
  },
  description: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramStory.description,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramStory.description,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.instagramStory.description,
  },
  aspectRatio: {
    audioClipper:
      DESTINATION_PRESETS_DEFAULT_CONTENT.instagramStory.aspectRatio,
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramStory.aspectRatio,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramStory.aspectRatio,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.instagramStory.aspectRatio,
  },
  durationMs: {
    audioClipper: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramStory.durationMs,
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramStory.durationMs,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramStory.durationMs,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.instagramStory.durationMs,
  },
};

export const INSTAGRAM_POST_PRESET: DestinationPreset = {
  key: 'InstagramPost',
  type: 'post',
  icon: <PlusBold width="13px" color="white" />,
  title: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramPost.title,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramPost.title,
    videoTranscript: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramPost.title,
  },
  description: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramPost.description,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramPost.description,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.instagramPost.description,
  },
  aspectRatio: {
    audioClipper: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramPost.aspectRatio,
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramPost.aspectRatio,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramPost.aspectRatio,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.instagramPost.aspectRatio,
  },
  durationMs: {
    audioClipper: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramPost.durationMs,
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramPost.durationMs,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.instagramPost.durationMs,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.instagramPost.durationMs,
  },
};

export const FACEBOOK_REEL_PRESET: DestinationPreset = {
  key: 'FacebookReel',
  type: 'reel',
  icon: <InstagramReels width="15px" color="white" />,
  title: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookReel.title,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookReel.title,
    videoTranscript: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookReel.title,
  },
  description: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookReel.description,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookReel.description,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.facebookReel.description,
  },
  aspectRatio: {
    audioClipper: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookReel.aspectRatio,
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookReel.aspectRatio,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookReel.aspectRatio,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.facebookReel.aspectRatio,
  },
  durationMs: {
    audioClipper: DESTINATION_PRESETS_DURATION_MILLIS.NINETY_SECS,
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookReel.durationMs,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookReel.durationMs,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.facebookReel.durationMs,
  },
};

export const FACEBOOK_STORY_PRESET: DestinationPreset = {
  key: 'FacebookStory',
  type: 'story',
  icon: <InstagramStories width="15px" color="white" />,
  title: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookStory.title,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookStory.title,
    videoTranscript: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookStory.title,
  },
  description: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookStory.description,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookStory.description,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.facebookStory.description,
  },
  aspectRatio: {
    audioClipper: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookStory.aspectRatio,
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookStory.aspectRatio,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookStory.aspectRatio,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.facebookStory.aspectRatio,
  },
  durationMs: {
    audioClipper: DESTINATION_PRESETS_DURATION_MILLIS.TWENTY_SECS,
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookStory.durationMs,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookStory.durationMs,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.facebookStory.durationMs,
  },
};

export const FACEBOOK_POST_PRESET: DestinationPreset = {
  key: 'FacebookPost',
  type: 'post',
  icon: <PlusBold width="9px" color="white" />,
  title: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookPost.title,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookPost.title,
    videoTranscript: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookPost.title,
  },
  description: {
    audiogram: 'Create a full-length segment',
    automation: 'Create clips from your podcast',
    videoTranscript: 'Create full-length segments',
  },
  aspectRatio: {
    audioClipper: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookPost.aspectRatio,
    audiogram: 'landscape',
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookPost.aspectRatio,
    videoTranscript: 'landscape',
  },
  durationMs: {
    audioClipper: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookPost.durationMs,
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookPost.durationMs,
    automation: DESTINATION_PRESETS_DURATION_MILLIS.SIXTY_SECS,
    videoTranscript: DESTINATION_PRESETS_DURATION_MILLIS.THREE_MINUTES,
  },
};

export const FACEBOOK_EPISODE_PRESET: DestinationPreset = {
  key: 'FacebookEpisode',
  type: 'episode',
  icon: <Facebook width="12px" color="white" />,
  title: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookEpisode.title,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookEpisode.title,
  },
  description: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookEpisode.description,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookEpisode.description,
  },
  aspectRatio: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookEpisode.aspectRatio,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.facebookEpisode.aspectRatio,
  },
};

export const TIKTOK_POST_PRESET: DestinationPreset = {
  key: 'TikTokPost',
  type: 'post',
  icon: <TiktokLogo width="13px" />,
  title: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokPost.title,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokPost.title,
    videoTranscript: DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokPost.title,
  },
  description: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokPost.description,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokPost.description,
    videoTranscript: DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokPost.description,
  },
  aspectRatio: {
    audioClipper: DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokPost.aspectRatio,
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokPost.aspectRatio,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokPost.aspectRatio,
    videoTranscript: DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokPost.aspectRatio,
  },
  durationMs: {
    audioClipper: DESTINATION_PRESETS_DURATION_MILLIS.TEN_MINUTES,
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokPost.durationMs,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokPost.durationMs,
    videoTranscript: DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokPost.durationMs,
  },
};

export const TIKTOK_STORY_PRESET: DestinationPreset = {
  key: 'TikTokStory',
  type: 'story',
  icon: <TiktokLogo width="13px" />,
  title: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokStory.title,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokStory.title,
    videoTranscript: DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokStory.title,
  },
  description: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokStory.description,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokStory.description,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokStory.description,
  },
  aspectRatio: {
    audioClipper: DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokStory.aspectRatio,
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokStory.aspectRatio,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokStory.aspectRatio,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokStory.aspectRatio,
  },
  durationMs: {
    audioClipper: DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokStory.durationMs,
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokStory.durationMs,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokStory.durationMs,
    videoTranscript: DESTINATION_PRESETS_DEFAULT_CONTENT.tikTokStory.durationMs,
  },
};

export const LINKEDIN_FEED_VIDEO_PRESET: DestinationPreset = {
  key: 'LinkedInFeedVideo',
  type: 'feedVideo',
  title: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInFeedVideo.title,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInFeedVideo.title,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInFeedVideo.title,
  },
  description: {
    audiogram:
      DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInFeedVideo.description,
    automation:
      DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInFeedVideo.description,
    videoTranscript: 'Create full-length segments',
  },
  aspectRatio: {
    audiogram:
      DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInFeedVideo.aspectRatio,
    automation:
      DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInFeedVideo.aspectRatio,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInFeedVideo.aspectRatio,
  },
  durationMs: {
    audiogram: DESTINATION_PRESETS_DURATION_MILLIS.TEN_MINUTES,
    automation: DESTINATION_PRESETS_DURATION_MILLIS.NINETY_SECS,
    videoTranscript: DESTINATION_PRESETS_DURATION_MILLIS.THREE_MINUTES,
  },
};

export const LINKEDIN_POST_PRESET: DestinationPreset = {
  key: 'LinkedInPost',
  type: 'post',
  title: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInPost.title,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInPost.title,
    videoTranscript: DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInPost.title,
  },
  description: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInPost.description,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInPost.description,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInPost.description,
  },
  aspectRatio: {
    audioClipper: DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInPost.aspectRatio,
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInPost.aspectRatio,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInPost.aspectRatio,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInPost.aspectRatio,
  },
  durationMs: {
    audioClipper: DESTINATION_PRESETS_DURATION_MILLIS.TEN_MINUTES,
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInPost.durationMs,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInPost.durationMs,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInPost.durationMs,
  },
};

export const LINKEDIN_SHORT_PRESET: DestinationPreset = {
  key: 'LinkedInShort',
  type: 'short',
  title: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInShort.title,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInShort.title,
    videoTranscript: DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInShort.title,
  },
  description: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInShort.description,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInShort.description,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInShort.description,
  },
  aspectRatio: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInShort.aspectRatio,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInShort.aspectRatio,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInShort.aspectRatio,
  },
  durationMs: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInShort.durationMs,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInShort.durationMs,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.linkedInShort.durationMs,
  },
};

export const THREADS_REEL_PRESET: DestinationPreset = {
  key: 'ThreadsReel',
  type: 'reel',
  title: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.threadsReel.title,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.threadsReel.title,
    videoTranscript: DESTINATION_PRESETS_DEFAULT_CONTENT.threadsReel.title,
  },
  description: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.threadsReel.description,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.threadsReel.description,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.threadsReel.description,
  },
  aspectRatio: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.threadsReel.aspectRatio,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.threadsReel.aspectRatio,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.threadsReel.aspectRatio,
  },
  durationMs: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.threadsReel.durationMs,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.threadsReel.durationMs,
    videoTranscript: DESTINATION_PRESETS_DEFAULT_CONTENT.threadsReel.durationMs,
  },
};

export const THREADS_POST_PRESET: DestinationPreset = {
  key: 'ThreadsPost',
  type: 'post',
  title: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.threadsPost.title,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.threadsPost.title,
    videoTranscript: DESTINATION_PRESETS_DEFAULT_CONTENT.threadsPost.title,
  },
  description: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.threadsPost.description,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.threadsPost.description,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.threadsPost.description,
  },
  aspectRatio: {
    audioClipper: DESTINATION_PRESETS_DEFAULT_CONTENT.threadsPost.aspectRatio,
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.threadsPost.aspectRatio,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.threadsPost.aspectRatio,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.threadsPost.aspectRatio,
  },
  durationMs: {
    audioClipper: DESTINATION_PRESETS_DEFAULT_CONTENT.threadsPost.durationMs,
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.threadsPost.durationMs,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.threadsPost.durationMs,
    videoTranscript: DESTINATION_PRESETS_DEFAULT_CONTENT.threadsPost.durationMs,
  },
};

export const TWITTER_STANDARD_TIMELINE_PRESET: DestinationPreset = {
  key: 'TwitterStandardTimeline',
  type: 'standardTimeline',
  title: {
    audiogram:
      DESTINATION_PRESETS_DEFAULT_CONTENT.twitterStandardTimeline.title,
    automation:
      DESTINATION_PRESETS_DEFAULT_CONTENT.twitterStandardTimeline.title,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.twitterStandardTimeline.title,
  },
  description: {
    audiogram:
      DESTINATION_PRESETS_DEFAULT_CONTENT.twitterStandardTimeline.description,
    automation:
      DESTINATION_PRESETS_DEFAULT_CONTENT.twitterStandardTimeline.description,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.twitterStandardTimeline.description,
  },
  aspectRatio: {
    audiogram:
      DESTINATION_PRESETS_DEFAULT_CONTENT.twitterStandardTimeline.aspectRatio,
    automation:
      DESTINATION_PRESETS_DEFAULT_CONTENT.twitterStandardTimeline.aspectRatio,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.twitterStandardTimeline.aspectRatio,
  },
  durationMs: {
    audiogram:
      DESTINATION_PRESETS_DEFAULT_CONTENT.twitterStandardTimeline.durationMs,
    automation:
      DESTINATION_PRESETS_DEFAULT_CONTENT.twitterStandardTimeline.durationMs,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.twitterStandardTimeline.durationMs,
  },
};

export const TWITTER_POST_PRESET: DestinationPreset = {
  key: 'TwitterPost',
  type: 'post',
  title: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.twitterPost.title,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.twitterPost.title,
    videoTranscript: DESTINATION_PRESETS_DEFAULT_CONTENT.twitterPost.title,
  },
  description: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.twitterPost.description,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.twitterPost.description,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.twitterPost.description,
  },
  aspectRatio: {
    audioClipper: DESTINATION_PRESETS_DEFAULT_CONTENT.twitterPost.aspectRatio,
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.twitterPost.aspectRatio,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.twitterPost.aspectRatio,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.twitterPost.aspectRatio,
  },
  durationMs: {
    audioClipper: DESTINATION_PRESETS_DURATION_MILLIS.TWO_MINUTES_TWENTY_SECS,
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.twitterPost.durationMs,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.twitterPost.durationMs,
    videoTranscript: DESTINATION_PRESETS_DEFAULT_CONTENT.twitterPost.durationMs,
  },
};

export const TWITTER_VIDEO_TAB_PRESET: DestinationPreset = {
  key: 'TwitterVideoTab',
  type: 'videoTab',
  title: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.twitterVideoTab.title,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.twitterVideoTab.title,
    videoTranscript: DESTINATION_PRESETS_DEFAULT_CONTENT.twitterVideoTab.title,
  },
  description: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.twitterVideoTab.description,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.twitterVideoTab.description,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.twitterVideoTab.description,
  },
  aspectRatio: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.twitterVideoTab.aspectRatio,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.twitterVideoTab.aspectRatio,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.twitterVideoTab.aspectRatio,
  },
  durationMs: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.twitterVideoTab.durationMs,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.twitterVideoTab.durationMs,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.twitterVideoTab.durationMs,
  },
};

export const BLUESKY_POST_PRESET: DestinationPreset = {
  key: 'BlueskyPost',
  type: 'post',
  title: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyPost.title,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyPost.title,
    videoTranscript: DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyPost.title,
  },
  description: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyPost.description,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyPost.description,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyPost.description,
  },
  aspectRatio: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyPost.aspectRatio,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyPost.aspectRatio,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyPost.aspectRatio,
  },
  durationMs: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyPost.durationMs,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyPost.durationMs,
    videoTranscript: DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyPost.durationMs,
  },
};

export const BLUESKY_SHORT_CLIP_PRESET: DestinationPreset = {
  key: 'BlueskyShortClip',
  type: 'shortClip',
  title: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyShortClip.title,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyShortClip.title,
    videoTranscript: DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyShortClip.title,
  },
  description: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyShortClip.description,
    automation:
      DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyShortClip.description,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyShortClip.description,
  },
  aspectRatio: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyShortClip.aspectRatio,
    automation:
      DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyShortClip.aspectRatio,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyShortClip.aspectRatio,
  },
  durationMs: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyShortClip.durationMs,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyShortClip.durationMs,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyShortClip.durationMs,
  },
};

export const BLUESKY_VIDEO_FEEDS_PRESET: DestinationPreset = {
  key: 'BlueskyVideoFeeds',
  type: 'videoFeeds',
  title: {
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyVideoFeeds.title,
    automation: DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyVideoFeeds.title,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyVideoFeeds.title,
  },
  description: {
    audiogram:
      DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyVideoFeeds.description,
    automation:
      DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyVideoFeeds.description,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyVideoFeeds.description,
  },
  aspectRatio: {
    audioClipper:
      DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyVideoFeeds.aspectRatio,
    audiogram:
      DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyVideoFeeds.aspectRatio,
    automation:
      DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyVideoFeeds.aspectRatio,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyVideoFeeds.aspectRatio,
  },
  durationMs: {
    audioClipper: DESTINATION_PRESETS_DURATION_MILLIS.SIXTY_SECS,
    audiogram: DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyVideoFeeds.durationMs,
    automation:
      DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyVideoFeeds.durationMs,
    videoTranscript:
      DESTINATION_PRESETS_DEFAULT_CONTENT.blueskyVideoFeeds.durationMs,
  },
};

export const PINTEREST_PRESET: DestinationPreset = {
  key: 'Pinterest',
  type: 'post',
  aspectRatio: {
    audioClipper: 'square',
  },
  durationMs: {
    audioClipper: DESTINATION_PRESETS_DURATION_MILLIS.TEN_MINUTES,
  },
};

export const SNAPCHAT_PRESET: DestinationPreset = {
  key: 'SnapChat',
  type: 'post',
  aspectRatio: {
    audioClipper: 'square',
  },
  durationMs: {
    audioClipper: DESTINATION_PRESETS_DURATION_MILLIS.SIXTY_SECS,
  },
};

export const FULLCLIP_PRESET: DestinationPreset = {
  key: 'FullClip',
  type: 'clip',
  aspectRatio: {
    audioClipper: 'square',
  },
  durationMs: {
    audioClipper: DESTINATION_PRESETS_DURATION_MILLIS.TEN_MINUTES,
  },
};

export const DESTINATION_PRESETS: DestinationPresetsConfig = {
  youtube: {
    episode: YOUTUBE_EPISODE_PRESET,
    clip: YOUTUBE_CLIP_PRESET,
    short: YOUTUBE_SHORT_PRESET,
  },
  instagram: {
    reel: INSTAGRAM_REEL_PRESET,
    story: INSTAGRAM_STORY_PRESET,
    post: INSTAGRAM_POST_PRESET,
  },
  facebook: {
    reel: FACEBOOK_REEL_PRESET,
    story: FACEBOOK_STORY_PRESET,
    post: FACEBOOK_POST_PRESET,
    episode: FACEBOOK_EPISODE_PRESET,
  },
  tiktok: {
    post: TIKTOK_POST_PRESET,
    story: TIKTOK_STORY_PRESET,
  },
  linkedin: {
    feedVideo: LINKEDIN_FEED_VIDEO_PRESET,
    post: LINKEDIN_POST_PRESET,
    short: LINKEDIN_SHORT_PRESET,
  },
  threads: {
    reel: THREADS_REEL_PRESET,
    post: THREADS_POST_PRESET,
  },
  twitter: {
    standardTimeline: TWITTER_STANDARD_TIMELINE_PRESET,
    post: TWITTER_POST_PRESET,
    videoTab: TWITTER_VIDEO_TAB_PRESET,
  },
  bluesky: {
    post: BLUESKY_POST_PRESET,
    shortClip: BLUESKY_SHORT_CLIP_PRESET,
    videoFeeds: BLUESKY_VIDEO_FEEDS_PRESET,
  },
  more: {
    snapchat: SNAPCHAT_PRESET,
    pinterest: PINTEREST_PRESET,
  },
};

export const PLATFORMS_CONFIG: DestinationPlatformMap<PlatformOptions> = {
  youtube: {
    id: 'youtube',
    icon: <YouTubeSocialCircle className="white-background" />,
    videoTypeIconBackground: HEADLINER_GRADIENT,
  },
  instagram: {
    id: 'instagram',
    icon: <InstagramSocialCircle />,
    videoTypeIconBackground: INSTAGRAM_GRADIENT,
  },
  facebook: {
    id: 'facebook',
    icon: <FacebookSocialCircle />,
    videoTypeIconBackground: FACEBOOK_BLUE,
  },
  tiktok: {
    id: 'tiktok',
    icon: <TikTokSocialCircle />,
    videoTypeIconBackground: BLACK,
  },
  linkedin: {
    id: 'linkedin',
    icon: <LinkedinSocialCircle className="white-details" />,
    videoTypeIconBackground: HEADLINER_GRADIENT,
  },
  threads: {
    id: 'threads',
    icon: <ThreadsSocialCircle />,
    videoTypeIconBackground: HEADLINER_GRADIENT,
  },
  twitter: {
    id: 'twitter',
    icon: <XSocialCircle />,
    videoTypeIconBackground: HEADLINER_GRADIENT,
  },
  bluesky: {
    id: 'bluesky',
    icon: <BlueskySocialCircle color="#1085fe" className="bluesky" />,
    videoTypeIconBackground: HEADLINER_GRADIENT,
  },
};
